import moment from 'moment';

import { BacklinksOverviewResult, ChartData, ArrayElement, IconProperties, BacklinksResource } from './types';

/**
 * @param overview the overview data
 * @param resourceType resource type from the overview data to get the trend of
 * @returns a properties object to be used in the Icon element
 */
export function getTrendProperties(overview: BacklinksOverviewResult, resourceType: keyof BacklinksOverviewResult): IconProperties {
	if (overview && resourceType in overview && overview[resourceType].length) {
		let count = overview[resourceType].length;

		if (count > 1) {
			let first = overview[resourceType][0]['value'];
			let last = overview[resourceType][count - 1]['value'];

			if (first < last) {
				return {
					type: 'arrow-up',
					style: { color: '#3f8600' },
				};
			} else if (first > last) {
				return {
					type: 'arrow-down',
					style: { color: '#cf1322' },
				};
			}
		}
	}
	return { type: 'swap', style: undefined };
}

// Tooltips for the chart
export const tooltipLabels = {
	total_backlinks: 'Total Backlinks',
	referring_domains: 'Referring Domains',
	domain_score: 'Domain Score',
	follow_links: 'Follow Links',
	nofollow_links: 'Nofollow links',
};

/**
 * Will return the latest overview data for a given resource type
 * @param overview All overview data
 * @param resourceType A type of overview data (key of type BacklinksOverviewObject)
 * @returns a string representing the most recent data
 */
export function getMostRecentData(overview: BacklinksOverviewResult, resourceType: keyof BacklinksOverviewResult) {
	if (overview && resourceType in overview && overview[resourceType].length) {
		const resourceData = overview[resourceType];
		const lastData = resourceData[resourceData.length - 1];
		const value = lastData.value;

		return value;
	}
	return 0;
}

/**
 * @param overview All overview data
 * @param resourceType A type of overview data
 * @returns the chart data to be used for the specific resource type
 */
export function getChartData(overview: BacklinksOverviewResult, resourceType: keyof BacklinksOverviewResult) {
	let overviewData: ChartData =
		overview && overview[resourceType] ? overview[resourceType].map((item) => ({ ...item, type: tooltipLabels[resourceType] })) : [];

	return getVisibleOverviewData(overviewData);
}

function getVisibleOverviewData(overviewData: ChartData) {
	const pointsInReverse = [...overviewData];
	pointsInReverse.reverse();

	const weeklyPoints = [];
	weeklyPoints.unshift(pointsInReverse[0]);

	let nextDesiredMoment = pointMoment(pointsInReverse[0]).subtract(1, 'weeks');
	pointsInReverse.forEach((element) => {
		const elCreatedAt = pointMoment(element);
		// data from server comes every 24h +/- a couple of seconds/minutes
		if (!elCreatedAt.isAfter(nextDesiredMoment, 'day')) {
			weeklyPoints.unshift(element);
			nextDesiredMoment = pointMoment(element).subtract(1, 'weeks');
		}
	});
	return weeklyPoints.slice(-4) as ChartData;
}

function pointMoment(point: ArrayElement<ChartData>) {
	return moment(point.c_at);
}

export const getResourceToExportAsJson = (data: BacklinksResource<any>) => {
	if (typeof data !== 'undefined') {
		return JSON.stringify(data.data);
	}
};

export const getResourceToExportAsCsv = (data: BacklinksResource<any>) => {
	if (typeof data !== 'undefined') {
		let csv = '';
		try {
			let columns = Object.keys(data.data[0]);
			columns.forEach((column) => {
				csv = `${csv}${column};`;
			});
			csv = csv.replace(/.$/, '\n');

			data.data.forEach((element) => {
				columns.forEach((column) => {
					// @ts-ignore todo fix typing
					let value = element[column] || '';
					csv = `${csv}${value};`;
				});
				csv = csv.replace(/.$/, '\n');
			});
		} catch (e) {}
		return csv;
	}
};

export function tickFormatter(val: number) {
	if (val >= 1000000000) {
		val = val / 1000000000;
		return val.toFixed(1) + ' B';
	}
	if (val >= 1000000) {
		val = val / 1000000;
		return val.toFixed(1) + ' M';
	}
	if (val >= 100000) {
		val = val / 1000000;
		return val.toFixed(1) + ' M';
	}
	if (val >= 1000) {
		val = val / 1000;

		if (val < 10) {
			return val.toFixed(1) + ' K';
		}

		return val.toFixed(0) + ' K';
	}

	return val;
}
